import {ClassOf} from "../types/class-of.type";
import {Entity} from "../domain/entities/entity";

export class ArrayHelper {

    static isEqual<S = any, T = Array<S>>(array1: T | null, array2: T | null): boolean {
        if ([array1, array2].some(array => !Array.isArray(array))) {
            return false;
        }

        if ((array1 as any[]).length !== (array2 as any[]).length) {
            return false;
        }

        if (!(array1 as S[]).every(array1Value => (array2 as S[]).includes(array1Value))) {
            return false;
        }

        return (array2 as S[]).every(array2Value => (array1 as S[]).includes(array2Value));
    }

    public static first<T>(values: Iterable<T>): T | null {
        // noinspection LoopStatementThatDoesntLoopJS
        for (const value of Array.from(values).values()) {
            return value;
        }

        return null;
    }

    public static notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
        return !(value === null || value === undefined);
    }

    public static last<T = any>(array: T[]): T | null {
        return [...array].pop() ?? null;
    }

    public static intersect<T>(array1: T[], array2: T[]): T[] {
        return [...(new Set(array1)).intersection(new Set(array2))];
    }

    public static range(size: number, startAt = 0): number[] {
        return [...Array(size).keys()].map(i => i + startAt);
    }

    static unique<T>(array: T[], accessFn?: ((item: T) => any) | null): T[] {
        if (accessFn) {
            const map = new Map();
            for (const item of array) {
                const value = accessFn(item);
                if (!map.has(value)) {
                    map.set(value, item);
                }
            }

            return Array.from(map.values());
        }

        return [...new Set(array)];
    }

    static uniqueEntities<T extends Entity>(entities: T[]): T[] {
        if (entities.length === 0) {
            return [];
        }
        const entityClass = ArrayHelper.first(entities)!.constructor as ClassOf<T>;

        return entities.filter(entity => entity.constructor === entityClass);
    }

    static async filterAsync<TObject>(objects: TObject[], filterFn: (object: TObject) => Promise<boolean>): Promise<TObject[]> {
        const filterPromises = objects.map(async obj => {
            try {
                return await filterFn(obj);
            } catch (error) {
                console.error(`Fehler beim Filtern des Objekts:`, obj, error);
                return false; // im Fehlerfall das Objekt herausfiltern
            }
        });

        const filterResults = await Promise.all(filterPromises);

        return objects.filter((_, index) => filterResults[index]);
    }
}